import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Header } from "@/components/brand/Header";
import { Footer } from "@/components/brand/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import { KAFFA } from "@/constants/testIds";
import { toast } from "sonner";
import { Coffee, MapPin, Award, FileText, Sparkles, CheckCircle2, ArrowLeft, Mountain, Droplets, Calendar, Building2, PackageOpen } from "lucide-react";

export default function LotDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currency, formatPrice, formatDate, t, tData } = useLocale();
  const [lot, setLot] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openProposal, setOpenProposal] = useState(false);
  const [proposal, setProposal] = useState({ proposed_price_per_bag: "", bags_quantity: "", message: "" });
  const [interestBags, setInterestBags] = useState("");
  const [openInterest, setOpenInterest] = useState(false);
  const [shippingForm, setShippingForm] = useState({
    use_profile: true,
    shipping_recipient: "",
    shipping_address: "",
    shipping_city: "",
    shipping_state: "",
    shipping_country: "",
    shipping_postal_code: "",
    shipping_phone: "",
    notes: "",
  });

  const isApprovedBuyer = user && user.role === "buyer" && user.status === "approved";
  const isAdmin = user && user.role === "admin";

  useEffect(() => {
    if (!user) return;
    if (user === false) {
      navigate(`/login?next=/lot/${id}`);
      return;
    }
    if (user.role === "buyer" && user.status !== "approved") {
      navigate("/pending");
      return;
    }
    setLoading(true);
    Promise.all([
      api.get(`/lots/${id}`, { params: { currency } }),
      api.get(`/lots/${id}/price-history`).catch(() => ({ data: [] })),
    ]).then(([r1, r2]) => { setLot(r1.data); setHistory(r2.data || []); })
      .catch(() => toast.error(t("lot.not_found")))
      .finally(() => setLoading(false));
  }, [id, user, navigate, currency]);

  const submitProposal = async () => {
    try {
      await api.post("/proposals", {
        lot_id: id,
        proposed_price_per_bag: Number(proposal.proposed_price_per_bag),
        bags_quantity: Number(proposal.bags_quantity),
        currency: lot.currency,
        message: proposal.message,
      });
      toast.success(t("lot.proposal_sent"));
      setOpenProposal(false);
      setProposal({ proposed_price_per_bag: "", bags_quantity: "", message: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const submitInterest = async () => {
    const errors = [];
    if (!interestBags || Number(interestBags) < 1) errors.push(t("sample.error_bags"));
    if (!shippingForm.use_profile && (!shippingForm.shipping_address || !shippingForm.shipping_city || !shippingForm.shipping_country)) {
      errors.push(t("sample.error_address"));
    }
    if (errors.length > 0) {
      toast.error(errors.join(" · "));
      return;
    }
    try {
      const payload = {
        lot_id: id,
        bags_quantity: Number(interestBags),
        message: shippingForm.notes?.trim() || t("lot.interest_default_msg"),
      };
      if (!shippingForm.use_profile) {
        Object.assign(payload, {
          shipping_recipient: shippingForm.shipping_recipient || null,
          shipping_address: shippingForm.shipping_address,
          shipping_city: shippingForm.shipping_city,
          shipping_state: shippingForm.shipping_state || null,
          shipping_country: shippingForm.shipping_country,
          shipping_postal_code: shippingForm.shipping_postal_code || null,
          shipping_phone: shippingForm.shipping_phone || null,
        });
      }
      await api.post("/interests", payload);
      toast.success(t("sample.success_toast"), {
        description: t("sample.success_description"),
        duration: 6500,
      });
      setOpenInterest(false);
      setInterestBags("");
      setShippingForm({
        use_profile: true,
        shipping_recipient: "", shipping_address: "", shipping_city: "",
        shipping_state: "", shipping_country: "", shipping_postal_code: "",
        shipping_phone: "", notes: "",
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (loading || !lot) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <div className="flex-1 flex items-center justify-center text-muted-foreground font-mono-data text-xs uppercase tracking-wider">
          {t("common.loading")}
        </div>
        <Footer />
      </div>
    );
  }

  const displayPrice = lot.display_price ?? lot.price_per_bag;
  const displayCurrency = lot.display_currency ?? lot.currency;
  const chartData = history.map((h) => ({
    date: formatDate(h.created_at),
    price: h.price_per_bag,
  }));

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      <main className="flex-1 mx-auto max-w-[1500px] w-full px-4 sm:px-8 py-8">
        <Link to="/marketplace" className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-accent flex items-center gap-1.5 mb-6">
          <ArrowLeft className="h-3 w-3" /> {t("nav.marketplace")}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Image gallery */}
          <div className="lg:col-span-7">
            <div className="aspect-[16/10] border border-border overflow-hidden">
              <img src={lot.images?.[0]} alt={lot.name} className="h-full w-full object-cover" />
            </div>
            {lot.images?.length > 1 && (
              <div className="grid grid-cols-4 gap-2 mt-2">
                {lot.images.slice(1, 5).map((img, i) => (
                  <div key={i} className="aspect-square border border-border overflow-hidden">
                    <img src={img} alt="" className="h-full w-full object-cover" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="px-2 py-0.5 text-[10px] font-mono-data uppercase tracking-wider border border-accent/40 bg-accent/10 text-accent">
                {tData("quality", lot.quality_grade)}
              </span>
              <span className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono-data uppercase tracking-wider border border-border bg-secondary">
                <Sparkles className="h-2.5 w-2.5" /> SCA {lot.sca_score}
              </span>
              <span className="px-2 py-0.5 text-[10px] font-mono-data uppercase tracking-wider border border-border bg-secondary">
                {tData("process", lot.process)}
              </span>
            </div>

            <h1 data-testid={KAFFA.lotTitle} className="font-display text-3xl sm:text-4xl tracking-tighter font-black leading-[1.05]">
              {lot.name}
            </h1>

            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
              <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {lot.farm_name}</span>
              <span>·</span>
              <span>{lot.city}, {lot.state} · {lot.origin_country}</span>
            </div>

            <div className="mt-8 border border-border bg-card p-6">
              <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                {t("lot.bag_price")} · {displayCurrency}
              </div>
              <div data-testid={KAFFA.lotPrice} className="font-display text-5xl tracking-tighter font-black text-accent mt-1">
                {formatPrice(displayPrice, displayCurrency)}
              </div>
              {lot.auto_pricing_enabled && (
                <div className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground mt-2 flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 bg-emerald-500 rounded-full animate-pulse" />
                  {t("lot.auto_priced")} {lot.margin_pct}%
                </div>
              )}
              <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-xs">
                <div>
                  <div className="text-muted-foreground">{t("lot.available_qty")}</div>
                  <div className="font-mono-data text-base">{lot.bags_quantity.toLocaleString()} {t("common.bags")}</div>
                </div>
                <div>
                  <div className="text-muted-foreground">{t("lot.total_volume")}</div>
                  <div className="font-mono-data text-base">{(lot.bags_quantity * lot.bag_weight_kg).toLocaleString()} kg</div>
                </div>
              </div>
            </div>

            {/* Actions */}
            {isApprovedBuyer && (
              <div className="mt-4 grid grid-cols-2 gap-3">
                <Dialog open={openInterest} onOpenChange={setOpenInterest}>
                  <DialogTrigger asChild>
                    <Button
                      data-testid={KAFFA.lotInterestBtn}
                      variant="outline"
                      className="h-12 rounded-none border-border hover:border-accent hover:text-accent font-mono-data text-xs uppercase tracking-[0.2em]"
                    >
                      <PackageOpen className="h-3.5 w-3.5 mr-2" /> {t("sample.request_btn")}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="rounded-none sm:max-w-xl max-h-[92vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="font-display text-2xl tracking-tight">{t("sample.dialog_title")}</DialogTitle>
                      <DialogDescription className="text-xs text-muted-foreground">
                        {t("sample.dialog_subtitle")}
                      </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 mt-2">
                      <div>
                        <Label className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">{t("lot.bags_qty")}</Label>
                        <Input
                          type="number" min={1} max={lot.bags_quantity}
                          value={interestBags}
                          onChange={(e) => setInterestBags(e.target.value)}
                          data-testid="sample-bags-qty"
                          className="mt-1.5 h-11 rounded-none"
                        />
                      </div>

                      {/* Shipping toggle */}
                      <div className="border border-border bg-secondary/30 p-4 space-y-3">
                        <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-accent flex items-center gap-2">
                          <MapPin className="h-3 w-3" /> {t("sample.shipping_title")}
                        </div>
                        <div className="flex flex-col gap-2 text-sm">
                          <label className="flex items-start gap-3 cursor-pointer">
                            <input
                              type="radio"
                              checked={shippingForm.use_profile}
                              onChange={() => setShippingForm({ ...shippingForm, use_profile: true })}
                              data-testid="sample-ship-use-profile"
                              className="mt-1 accent-[#c87520]"
                            />
                            <div>
                              <div className="font-medium">{t("sample.use_profile_address")}</div>
                              <div className="text-xs text-muted-foreground">
                                {user?.razao_social} · {user?.country}
                              </div>
                            </div>
                          </label>
                          <label className="flex items-start gap-3 cursor-pointer">
                            <input
                              type="radio"
                              checked={!shippingForm.use_profile}
                              onChange={() => setShippingForm({ ...shippingForm, use_profile: false })}
                              data-testid="sample-ship-use-new"
                              className="mt-1 accent-[#c87520]"
                            />
                            <span className="font-medium">{t("sample.use_new_address")}</span>
                          </label>
                        </div>

                        {!shippingForm.use_profile && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                            <div className="sm:col-span-2">
                              <Label className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">
                                {t("sample.field_recipient")}
                              </Label>
                              <Input
                                value={shippingForm.shipping_recipient}
                                onChange={(e) => setShippingForm({ ...shippingForm, shipping_recipient: e.target.value })}
                                data-testid="sample-ship-recipient"
                                className="mt-1.5 h-10 rounded-none"
                              />
                            </div>
                            <div className="sm:col-span-2">
                              <Label className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">
                                {t("sample.field_address")} <span className="text-accent">*</span>
                              </Label>
                              <Input
                                value={shippingForm.shipping_address}
                                onChange={(e) => setShippingForm({ ...shippingForm, shipping_address: e.target.value })}
                                placeholder={t("sample.field_address_placeholder")}
                                data-testid="sample-ship-address"
                                className="mt-1.5 h-10 rounded-none"
                              />
                            </div>
                            <div>
                              <Label className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">
                                {t("sample.field_city")} <span className="text-accent">*</span>
                              </Label>
                              <Input
                                value={shippingForm.shipping_city}
                                onChange={(e) => setShippingForm({ ...shippingForm, shipping_city: e.target.value })}
                                data-testid="sample-ship-city"
                                className="mt-1.5 h-10 rounded-none"
                              />
                            </div>
                            <div>
                              <Label className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">
                                {t("sample.field_state")}
                              </Label>
                              <Input
                                value={shippingForm.shipping_state}
                                onChange={(e) => setShippingForm({ ...shippingForm, shipping_state: e.target.value })}
                                className="mt-1.5 h-10 rounded-none"
                              />
                            </div>
                            <div>
                              <Label className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">
                                {t("sample.field_country")} <span className="text-accent">*</span>
                              </Label>
                              <Input
                                value={shippingForm.shipping_country}
                                onChange={(e) => setShippingForm({ ...shippingForm, shipping_country: e.target.value })}
                                data-testid="sample-ship-country"
                                className="mt-1.5 h-10 rounded-none"
                              />
                            </div>
                            <div>
                              <Label className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">
                                {t("sample.field_postal")}
                              </Label>
                              <Input
                                value={shippingForm.shipping_postal_code}
                                onChange={(e) => setShippingForm({ ...shippingForm, shipping_postal_code: e.target.value })}
                                className="mt-1.5 h-10 rounded-none"
                              />
                            </div>
                            <div className="sm:col-span-2">
                              <Label className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">
                                {t("sample.field_phone")}
                              </Label>
                              <Input
                                value={shippingForm.shipping_phone}
                                onChange={(e) => setShippingForm({ ...shippingForm, shipping_phone: e.target.value })}
                                className="mt-1.5 h-10 rounded-none"
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      <div>
                        <Label className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">
                          {t("sample.field_notes")} ({t("common.optional")})
                        </Label>
                        <Textarea
                          rows={3}
                          value={shippingForm.notes}
                          onChange={(e) => setShippingForm({ ...shippingForm, notes: e.target.value })}
                          placeholder={t("sample.field_notes_placeholder")}
                          className="mt-1.5 rounded-none"
                        />
                      </div>
                    </div>

                    <DialogFooter className="mt-4">
                      <Button variant="ghost" onClick={() => setOpenInterest(false)} className="rounded-none">{t("common.cancel")}</Button>
                      <Button
                        onClick={submitInterest}
                        data-testid="sample-request-submit"
                        className="rounded-none bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground font-mono-data text-xs uppercase tracking-wider"
                      >
                        {t("sample.send_btn")}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                <Dialog open={openProposal} onOpenChange={setOpenProposal}>
                  <DialogTrigger asChild>
                    <Button
                      data-testid={KAFFA.lotProposalBtn}
                      className="h-12 rounded-none bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground font-mono-data text-xs uppercase tracking-[0.2em]"
                    >
                      {t("lot.propose")}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="rounded-none sm:max-w-lg">
                    <DialogHeader>
                      <DialogTitle className="font-display text-2xl tracking-tight">{t("lot.propose_title")}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label className="font-mono-data text-[10px] uppercase tracking-wider">
                          {t("lot.price_label")} ({lot.currency})
                        </Label>
                        <Input
                          data-testid={KAFFA.proposalPriceInput}
                          type="number" step="0.01"
                          value={proposal.proposed_price_per_bag}
                          onChange={(e) => setProposal({ ...proposal, proposed_price_per_bag: e.target.value })}
                          className="mt-1.5 h-11 rounded-none"
                          placeholder={String(lot.price_per_bag)}
                        />
                      </div>
                      <div>
                        <Label className="font-mono-data text-[10px] uppercase tracking-wider">{t("lot.bags_qty")}</Label>
                        <Input
                          data-testid={KAFFA.proposalBagsInput}
                          type="number" min={1} max={lot.bags_quantity}
                          value={proposal.bags_quantity}
                          onChange={(e) => setProposal({ ...proposal, bags_quantity: e.target.value })}
                          className="mt-1.5 h-11 rounded-none"
                        />
                      </div>
                      <div>
                        <Label className="font-mono-data text-[10px] uppercase tracking-wider">{t("lot.message_label")} ({t("common.optional")})</Label>
                        <Textarea
                          data-testid={KAFFA.proposalMessage}
                          value={proposal.message}
                          onChange={(e) => setProposal({ ...proposal, message: e.target.value })}
                          className="mt-1.5 rounded-none min-h-[100px]"
                          placeholder={t("lot.message_placeholder")}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button onClick={() => setOpenProposal(false)} variant="ghost" className="rounded-none">{t("common.cancel")}</Button>
                      <Button
                        onClick={submitProposal}
                        data-testid={KAFFA.proposalSubmit}
                        className="rounded-none bg-primary hover:bg-accent hover:text-accent-foreground"
                      >
                        {t("lot.send_proposal")}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            )}

            {/* Quick specs */}
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-px bg-border border border-border">
              {[
                { Icon: Mountain, label: t("lot.altitude"), value: `${lot.altitude_m}m` },
                { Icon: Award, label: t("lot.sieve"), value: lot.sieve },
                { Icon: Calendar, label: t("lot.crop"), value: lot.crop_year },
                { Icon: Droplets, label: t("lot.humidity"), value: `${lot.humidity_pct}%` },
                { Icon: Coffee, label: t("lot.blend"), value: lot.blend },
                { Icon: Building2, label: t("lot.fob"), value: lot.fob_port || "—" },
              ].map(({ Icon, label, value }, i) => (
                <div key={i} className="bg-card p-3">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Icon className="h-3 w-3" />
                    <span className="font-mono-data text-[10px] uppercase tracking-wider">{label}</span>
                  </div>
                  <div className="font-mono-data text-sm mt-1 truncate">{value || "—"}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Details tabs */}
        <div className="mt-12">
          <Tabs defaultValue="details" className="w-full">
            <TabsList className="rounded-none border border-border bg-card h-auto p-0">
              <TabsTrigger value="details" className="rounded-none px-6 py-3 font-mono-data text-xs uppercase tracking-wider data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
                {t("lot.tab_details")}
              </TabsTrigger>
              <TabsTrigger value="certs" className="rounded-none px-6 py-3 font-mono-data text-xs uppercase tracking-wider data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
                {t("lot.tab_certs")}
              </TabsTrigger>
              <TabsTrigger value="docs" className="rounded-none px-6 py-3 font-mono-data text-xs uppercase tracking-wider data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
                {t("lot.tab_docs")}
              </TabsTrigger>
              <TabsTrigger value="history" className="rounded-none px-6 py-3 font-mono-data text-xs uppercase tracking-wider data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">
                {t("lot.tab_history")}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="details" className="border border-t-0 border-border p-6 lg:p-10 bg-card">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div>
                  <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">{t("lot.origin")}</div>
                  <div className="font-display text-xl tracking-tight">{lot.farm_name}</div>
                  <div className="text-muted-foreground text-sm mt-1">{tData("region", lot.region)} · {lot.city}, {lot.state}</div>
                  <div className="text-muted-foreground text-sm">{lot.origin_country}</div>
                </div>
                <div>
                  <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">{t("lot.cooperative")}</div>
                  <div className="font-display text-xl tracking-tight">{lot.cooperative_name}</div>
                </div>
                <div>
                  <div className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">{t("lot.notes")}</div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{lot.notes || "—"}</p>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="certs" className="border border-t-0 border-border p-6 lg:p-10 bg-card">
              {lot.certifications?.length ? (
                <div className="flex flex-wrap gap-2">
                  {lot.certifications.map((c) => (
                    <span key={c} className="px-3 py-1.5 border border-accent/40 bg-accent/10 text-accent text-xs font-mono-data uppercase tracking-wider flex items-center gap-1.5">
                      <CheckCircle2 className="h-3 w-3" /> {c}
                    </span>
                  ))}
                </div>
              ) : <div className="text-muted-foreground text-sm">{t("lot.no_certs")}</div>}
            </TabsContent>
            <TabsContent value="docs" className="border border-t-0 border-border p-6 lg:p-10 bg-card">
              {lot.documents?.length ? (
                <ul className="divide-y divide-border">
                  {lot.documents.map((d, i) => (
                    <li key={i} className="py-3 flex items-center gap-3">
                      <FileText className="h-4 w-4 text-accent" />
                      <span className="text-sm">{d.name}</span>
                    </li>
                  ))}
                </ul>
              ) : <div className="text-muted-foreground text-sm">{t("lot.no_docs")}</div>}
            </TabsContent>
            <TabsContent value="history" className="border border-t-0 border-border p-6 lg:p-10 bg-card">
              {chartData.length > 0 ? (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" style={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} />
                      <YAxis stroke="hsl(var(--muted-foreground))" style={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} />
                      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))" }} />
                      <Line type="monotone" dataKey="price" stroke="hsl(var(--accent))" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-muted-foreground text-sm">{t("lot.no_history")}</div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>
      <Footer />
    </div>
  );
}
